/* eslint complexity: ["error", 11] */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useBrand } from '@cof/plastic-components';
import { jwtDecode } from 'jwt-decode';
import merge from 'lodash.merge';

import { useFormData as useFormDataFetch } from '../../data';
import { BTVendorCodes, CONSUMER_NAMES } from '../../utilities/constants';
import pushToDataLayer from '../../utilities/dataLayer/dataLayer';
import useQueryParams from '../../utilities/useQueryParams';
import useTechnicalError from '../../utilities/useTechnicalError';
import { getKey } from '../../utilities/formValuesMutation/formValuesMutation';
import partners from '../../partners';

// @ts-expect-error TS2554: Expected 1 arguments, but got 0.
export const FormDataContext = createContext();

export const resolveVendorCode = (parsedFormData: any, urlVendorCode: any, oemc: any) =>
    parsedFormData?.vendorCode || urlVendorCode || oemc || 'CQC';

const FormDataContextProvider = ({ children }: any) => {
    const [parsedFormData, setParsedFormData] = useState();
    const queryParams = useQueryParams();
    const [initialQueryParams] = useState(queryParams);
    const brand = useBrand();
    const consumerName = CONSUMER_NAMES[brand];
    const {
        oemc,
        vendorCode: urlVendorCode,
        sParameter,
        quotation,
        productId,
        pcr,
        upstreamTarget
    } = initialQueryParams;

    // @ts-expect-error TS2339: Property 'error' does not exist on type 'boolean | ((reqData: any) => void) |  error: string; respon
    const [execFormCall, formLoading, { error: formDataError, data: formDataResponse }] = useFormDataFetch(
        {
            // keys must be in alphabetical order unless query param ordering middleware is added to Tyk API definition
            consumerName,
            oemc,
            sParameter,
            vc: urlVendorCode,
            quotation,
            upstreamTarget
        },
        // @ts-expect-error TS2554: Expected 1 arguments, but got 2.
        parsedFormData
    );

    useEffect(() => {
        if (!['/ob-pending', '/ob-landing'].includes(window.location.pathname)) {
            // @ts-expect-error TS2349: This expression is not callable.
            execFormCall();
        }
    }, []);

    useTechnicalError(!quotation ? null : formDataError);

    const mergeResponseData = useCallback(
        (decodedFormDataResponse: any) => {
            const formDataConsumerName = decodedFormDataResponse?.consumerName;
            const formDataOemc = decodedFormDataResponse?.onlineEntryMethodCode;

            merge(decodedFormDataResponse, {
                consumerName: formDataConsumerName || consumerName,
                vendorCode: urlVendorCode || formDataOemc || oemc || 'CQC',
                oemc
            });
        },
        [consumerName, oemc, urlVendorCode]
    );

    useEffect(() => {
        if (!formDataResponse) return;
        // parse the form data
        const { formResponse } = formDataResponse;
        const formDataResponseDecoded = jwtDecode(formResponse);

        // @ts-expect-error TS2339: Property 'formValues' does not exist on type 'JwtPayload'.
        const { formValues } = formDataResponseDecoded;

        mergeResponseData(formDataResponseDecoded);

        // all information has been injected at this point
        // @ts-expect-error TS2345: Argument of type 'JwtPayload' is not assignable to parameter of type 'SetStateAction<undefined>'.
        setParsedFormData(formDataResponseDecoded);

        pushToDataLayer({
            ccsNumber: productId,
            // @ts-expect-error TS2339: Property 'vendorCode' does not exist on type 'JwtPayload'.
            vendorCode: formDataResponseDecoded.vendorCode,
            // @ts-expect-error TS2339: Property 'consumerName' does not exist on type 'JwtPayload'.
            consumerName: formDataResponseDecoded.consumerName,
            // @ts-expect-error TS2339: Property 'definition' does not exist on type 'JwtPayload'.
            definitionId: formDataResponseDecoded?.definition?.id,
            journeyType: formValues && Object.entries(formValues).length > 0 ? 'pre-pop' : 'standard',
            pcr
        });
    }, [formDataResponse, consumerName, oemc, pcr, productId, urlVendorCode, mergeResponseData, quotation, sParameter]);

    const isLoading = formLoading || parsedFormData == null;
    return (
        <FormDataContext.Provider
            value={{
                formData: parsedFormData,
                formResponse: formDataResponse?.formResponse,
                formLoading: isLoading,
                quotation,
                isEdit: !!quotation,
                vendorCode: resolveVendorCode(parsedFormData, urlVendorCode, oemc),
                isBT:
                    (BTVendorCodes.includes(oemc) && brand !== partners.ASOS) || // ASOS is the only hosted partner that does not offer BTs
                    // @ts-expect-error TS2339: Property 'formValues' does not exist on type 'never'.
                    !!(parsedFormData?.formValues && !!getKey('balance-transfer', parsedFormData?.formValues)),
                // @ts-expect-error TS2339: Property 'consumerName' does not exist on type 'never'.
                consumerName: parsedFormData?.consumerName || consumerName, // We should always use the consumer name from the initial quote if it exists
                xUpstreamEnv: upstreamTarget
            }}
        >
            {children}
        </FormDataContext.Provider>
    );
};
export const useFormData = () => useContext(FormDataContext);

export default FormDataContextProvider;

FormDataContextProvider.propTypes = {
    children: PropTypes.node.isRequired
};
